import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  RotateCw, Maximize2, Minimize2, FileText, FileSpreadsheet,
  File, Image, AlertTriangle,
} from 'lucide-react'
import type { Attachment } from '../../lib/types'

// ─── File type helpers ────────────────────────────────────────────────────────

type PreviewType = 'image' | 'pdf' | 'docx' | 'xlsx' | 'text' | 'unsupported'

function getPreviewType(att: Attachment): PreviewType {
  const { content_type, filename } = att
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''

  if (content_type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image'
  if (content_type === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'].includes(content_type) || ['docx', 'doc'].includes(ext)) return 'docx'
  if (['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'].includes(content_type) || ['xlsx', 'xls'].includes(ext)) return 'xlsx'
  if (content_type.startsWith('text/') || ['txt', 'md', 'csv', 'json', 'xml', 'yaml', 'yml', 'log', 'sh', 'js', 'ts', 'py', 'go', 'rs', 'css', 'html'].includes(ext)) return 'text'
  return 'unsupported'
}

function getFileIcon(type: PreviewType) {
  switch (type) {
    case 'image': return <Image size={20} />
    case 'pdf': return <FileText size={20} />
    case 'docx': return <FileText size={20} />
    case 'xlsx': return <FileSpreadsheet size={20} />
    case 'text': return <FileText size={20} />
    default: return <File size={20} />
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ─── Preview renderers ────────────────────────────────────────────────────────

interface RendererProps {
  attachment: Attachment
  blobUrl: string | null
  isLoading: boolean
  error: string | null
}

function ImageRenderer({ blobUrl, isLoading, error }: RendererProps) {
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [isFit, setIsFit] = useState(true)

  if (isLoading) return <div className="apv__loading"><div className="apv__spinner" /></div>
  if (error || !blobUrl) return <PreviewError message={error ?? 'Could not load image'} />

  const imgStyle: React.CSSProperties = isFit
    ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', transform: `rotate(${rotation}deg)` }
    : { transform: `scale(${zoom}) rotate(${rotation}deg)`, transformOrigin: 'center', transition: 'transform 0.15s ease' }

  return (
    <div className="apv__image-container">
      <div className="apv__image-scroll">
        <img src={blobUrl} alt="" className="apv__image" style={imgStyle} />
      </div>
      <div className="apv__image-toolbar">
        <button className="btn-icon" title="Zoom out" onClick={() => { setIsFit(false); setZoom(z => Math.max(0.25, z - 0.25)) }}><ZoomOut size={14} /></button>
        <span className="apv__zoom-label">{isFit ? 'fit' : `${Math.round(zoom * 100)}%`}</span>
        <button className="btn-icon" title="Zoom in" onClick={() => { setIsFit(false); setZoom(z => Math.min(4, z + 0.25)) }}><ZoomIn size={14} /></button>
        <div className="apv__sep" />
        <button className="btn-icon" title="Rotate" onClick={() => setRotation(r => r + 90)}><RotateCw size={14} /></button>
        <div className="apv__sep" />
        <button className="btn-icon" title={isFit ? 'Actual size' : 'Fit'} onClick={() => { setIsFit(f => !f); setZoom(1) }}>
          {isFit ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
        </button>
      </div>
    </div>
  )
}

function PdfRenderer({ blobUrl, isLoading, error }: RendererProps) {
  if (isLoading) return <div className="apv__loading"><div className="apv__spinner" /></div>
  if (error || !blobUrl) return <PreviewError message={error ?? 'Could not load PDF'} />
  return (
    <iframe
      src={blobUrl}
      className="apv__iframe"
      title="PDF preview"
      sandbox="allow-scripts allow-same-origin"
    />
  )
}

function DocxRenderer({ blobUrl, isLoading, error, attachment }: RendererProps & { attachment: Attachment }) {
  const [html, setHtml] = useState<string | null>(null)
  const [convertError, setConvertError] = useState<string | null>(null)
  const [converting, setConverting] = useState(false)

  useEffect(() => {
    if (!blobUrl || isLoading) return
    setConverting(true)
    setConvertError(null)

    const ext = attachment.filename.split('.').pop()?.toLowerCase()

    // .doc files can't be converted client-side — only .docx is supported by mammoth
    if (ext === 'doc') {
      setConvertError('Legacy .doc format cannot be previewed. Please download the file.')
      setConverting(false)
      return
    }

    fetch(blobUrl)
      .then(r => r.arrayBuffer())
      .then(async (buf) => {
        const mammoth = await import('mammoth')
        const result = await mammoth.convertToHtml({ arrayBuffer: buf })
        setHtml(result.value)
      })
      .catch(e => setConvertError(e.message))
      .finally(() => setConverting(false))
  }, [blobUrl, isLoading, attachment.filename])

  if (isLoading || converting) return <div className="apv__loading"><div className="apv__spinner" /></div>
  if (error || convertError) return <PreviewError message={error ?? convertError ?? 'Conversion failed'} />
  if (!html) return null

  return (
    <div className="apv__docx-container">
      <div className="apv__docx-body" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  )
}

function XlsxRenderer({ blobUrl, isLoading, error }: RendererProps) {
  const [tables, setTables] = useState<{ name: string; html: string }[]>([])
  const [activeSheet, setActiveSheet] = useState(0)
  const [convertError, setConvertError] = useState<string | null>(null)
  const [converting, setConverting] = useState(false)

  useEffect(() => {
    if (!blobUrl || isLoading) return
    setConverting(true)

    fetch(blobUrl)
      .then(r => r.arrayBuffer())
      .then(async (buf) => {
        const XLSX = await import('xlsx')
        const wb = XLSX.read(buf, { type: 'array' })
        const sheets = wb.SheetNames.map(name => ({
          name,
          html: XLSX.utils.sheet_to_html(wb.Sheets[name], { header: '', footer: '' }),
        }))
        setTables(sheets)
      })
      .catch(e => setConvertError(e.message))
      .finally(() => setConverting(false))
  }, [blobUrl, isLoading])

  if (isLoading || converting) return <div className="apv__loading"><div className="apv__spinner" /></div>
  if (error || convertError) return <PreviewError message={error ?? convertError ?? 'Conversion failed'} />
  if (!tables.length) return null

  return (
    <div className="apv__xlsx-container">
      {tables.length > 1 && (
        <div className="apv__sheet-tabs">
          {tables.map((t, i) => (
            <button
              key={t.name}
              className={`apv__sheet-tab ${i === activeSheet ? 'apv__sheet-tab--active' : ''}`}
              onClick={() => setActiveSheet(i)}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}
      <div
        className="apv__xlsx-body"
        dangerouslySetInnerHTML={{ __html: tables[activeSheet]?.html ?? '' }}
      />
    </div>
  )
}

function TextRenderer({ blobUrl, isLoading, error }: RendererProps) {
  const [text, setText] = useState<string | null>(null)

  useEffect(() => {
    if (!blobUrl || isLoading) return
    fetch(blobUrl).then(r => r.text()).then(setText).catch(() => setText(null))
  }, [blobUrl, isLoading])

  if (isLoading) return <div className="apv__loading"><div className="apv__spinner" /></div>
  if (error || !blobUrl) return <PreviewError message={error ?? 'Could not load file'} />
  if (text === null) return <div className="apv__loading"><div className="apv__spinner" /></div>

  return (
    <div className="apv__text-container">
      <pre className="apv__text-body">{text}</pre>
    </div>
  )
}

function PreviewError({ message }: { message: string }) {
  return (
    <div className="apv__error">
      <AlertTriangle size={32} />
      <p>{message}</p>
    </div>
  )
}

function UnsupportedRenderer({ attachment }: { attachment: Attachment }) {
  return (
    <div className="apv__unsupported">
      <File size={48} />
      <p className="apv__unsupported-name">{attachment.filename}</p>
      <p className="apv__unsupported-msg">Preview not available for this file type.</p>
      <p className="apv__unsupported-size">{formatBytes(attachment.size_bytes)}</p>
    </div>
  )
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export interface AttachmentPreviewProps {
  attachments: Attachment[]
  initialIndex: number
  onClose: () => void
  /** Called when the user wants to download an attachment. Receives attachment id. */
  onDownload?: (att: Attachment) => void
  /**
   * Called to resolve a URL/blob for a given attachment id.
   * In mock mode, pass a function returning a demo URL or data URI.
   * In production, this should hit /api/attachments/:id/download.
   */
  getAttachmentUrl?: (id: string) => Promise<string>
}

const DEMO_IMAGES = [
  'https://picsum.photos/seed/tmail1/800/600',
  'https://picsum.photos/seed/tmail2/1200/800',
  'https://picsum.photos/seed/tmail3/600/900',
]

async function defaultGetUrl(id: string): Promise<string> {
  // In mock/demo mode, return a sample image URL for image attachments
  // In production this would fetch from /api/attachments/:id/download
  const seed = parseInt(id.replace(/\D/g, '').slice(-2) || '1') % DEMO_IMAGES.length
  return DEMO_IMAGES[seed]
}

export function AttachmentPreviewModal({
  attachments,
  initialIndex,
  onClose,
  onDownload,
  getAttachmentUrl = defaultGetUrl,
}: AttachmentPreviewProps) {
  const [index, setIndex] = useState(initialIndex)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const prevUrlRef = useRef<string | null>(null)

  const att = attachments[index]
  const previewType = att ? getPreviewType(att) : 'unsupported'
  const total = attachments.length

  const loadAttachment = useCallback(async (attachment: Attachment) => {
    // Revoke previous blob url if any
    if (prevUrlRef.current) {
      URL.revokeObjectURL(prevUrlRef.current)
      prevUrlRef.current = null
    }
    setBlobUrl(null)
    setLoadError(null)
    setIsLoading(true)

    try {
      const url = await getAttachmentUrl(attachment.id)
      setBlobUrl(url)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load attachment')
    } finally {
      setIsLoading(false)
    }
  }, [getAttachmentUrl])

  useEffect(() => {
    if (att) loadAttachment(att)
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current)
    }
  }, [att, loadAttachment])

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') setIndex(i => Math.max(0, i - 1))
      if (e.key === 'ArrowRight') setIndex(i => Math.min(total - 1, i + 1))
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose, total])

  if (!att) return null

  const rendererProps: RendererProps = { attachment: att, blobUrl, isLoading, error: loadError }

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="apv__backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
      >
        <motion.div
          className="apv__modal"
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Header */}
          <div className="apv__header">
            <div className="apv__header-left">
              <span className="apv__file-icon">{getFileIcon(previewType)}</span>
              <div className="apv__header-info">
                <span className="apv__filename">{att.filename}</span>
                <span className="apv__meta">{formatBytes(att.size_bytes)}{total > 1 ? ` · ${index + 1} of ${total}` : ''}</span>
              </div>
            </div>
            <div className="apv__header-actions">
              {onDownload && (
                <button className="btn-icon" title="Download" onClick={() => onDownload(att)}>
                  <Download size={16} />
                </button>
              )}
              <button className="btn-icon" title="Close (Esc)" onClick={onClose}>
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Thumbnail strip (if multiple attachments) */}
          {total > 1 && (
            <div className="apv__strip">
              {attachments.map((a, i) => {
                const t = getPreviewType(a)
                return (
                  <button
                    key={a.id}
                    className={`apv__thumb ${i === index ? 'apv__thumb--active' : ''}`}
                    onClick={() => setIndex(i)}
                    title={a.filename}
                  >
                    <span className="apv__thumb-icon">{getFileIcon(t)}</span>
                    <span className="apv__thumb-name">{a.filename.length > 18 ? a.filename.slice(0, 15) + '…' : a.filename}</span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Preview area */}
          <div className="apv__body">
            {previewType === 'image' && <ImageRenderer {...rendererProps} />}
            {previewType === 'pdf' && <PdfRenderer {...rendererProps} />}
            {previewType === 'docx' && <DocxRenderer {...rendererProps} attachment={att} />}
            {previewType === 'xlsx' && <XlsxRenderer {...rendererProps} />}
            {previewType === 'text' && <TextRenderer {...rendererProps} />}
            {previewType === 'unsupported' && <UnsupportedRenderer attachment={att} />}
          </div>

          {/* Navigation arrows */}
          {total > 1 && (
            <>
              <button
                className="apv__nav apv__nav--prev"
                onClick={() => setIndex(i => Math.max(0, i - 1))}
                disabled={index === 0}
                title="Previous (←)"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                className="apv__nav apv__nav--next"
                onClick={() => setIndex(i => Math.min(total - 1, i + 1))}
                disabled={index === total - 1}
                title="Next (→)"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

// ─── CSS ──────────────────────────────────────────────────────────────────────

const css = `
  .apv__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.72);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  .apv__modal {
    background: var(--bg-panel);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    width: 100%;
    max-width: 900px;
    max-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    box-shadow: 0 24px 64px rgba(0,0,0,0.5);
  }

  .apv__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }

  .apv__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .apv__file-icon {
    color: var(--brand);
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  .apv__header-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .apv__filename {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .apv__meta {
    font-size: 11px;
    color: var(--text-tertiary);
  }

  .apv__header-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    margin-left: 12px;
  }

  .apv__strip {
    display: flex;
    gap: 6px;
    padding: 8px 16px;
    border-bottom: 1px solid var(--border);
    overflow-x: auto;
    flex-shrink: 0;
    scrollbar-width: thin;
  }

  .apv__thumb {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    border-radius: var(--radius-md);
    border: 1px solid var(--border);
    background: var(--bg-elevated);
    cursor: pointer;
    min-width: 80px;
    max-width: 100px;
    transition: background 0.1s, border-color 0.1s;
  }

  .apv__thumb:hover {
    background: var(--bg-hover);
  }

  .apv__thumb--active {
    border-color: var(--brand);
    background: color-mix(in srgb, var(--brand) 12%, transparent);
  }

  .apv__thumb-icon {
    color: var(--text-secondary);
    display: flex;
    align-items: center;
  }

  .apv__thumb-name {
    font-size: 10px;
    color: var(--text-secondary);
    text-align: center;
    word-break: break-all;
    line-height: 1.3;
  }

  .apv__body {
    flex: 1;
    overflow: hidden;
    position: relative;
    min-height: 300px;
  }

  /* Image */
  .apv__image-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .apv__image-scroll {
    flex: 1;
    overflow: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
  }

  .apv__image {
    display: block;
  }

  .apv__image-toolbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 12px;
    border-top: 1px solid var(--border);
    background: var(--bg-panel);
  }

  .apv__zoom-label {
    font-size: 11px;
    color: var(--text-secondary);
    min-width: 32px;
    text-align: center;
  }

  .apv__sep {
    width: 1px;
    height: 14px;
    background: var(--border);
    margin: 0 2px;
  }

  /* PDF */
  .apv__iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }

  /* DOCX */
  .apv__docx-container {
    height: 100%;
    overflow: auto;
    padding: 32px 48px;
    background: #fff;
  }

  .apv__docx-body {
    max-width: 740px;
    margin: 0 auto;
    font-family: 'Georgia', serif;
    font-size: 14px;
    line-height: 1.6;
    color: #1a1a1a;
  }

  .apv__docx-body h1, .apv__docx-body h2, .apv__docx-body h3 { margin: 1.2em 0 0.4em; }
  .apv__docx-body p { margin: 0.6em 0; }
  .apv__docx-body table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  .apv__docx-body td, .apv__docx-body th { border: 1px solid #ccc; padding: 6px 10px; }
  .apv__docx-body img { max-width: 100%; height: auto; }

  /* XLSX */
  .apv__xlsx-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .apv__sheet-tabs {
    display: flex;
    gap: 2px;
    padding: 8px 12px 0;
    border-bottom: 1px solid var(--border);
    background: var(--bg-elevated);
    flex-shrink: 0;
    overflow-x: auto;
  }

  .apv__sheet-tab {
    padding: 5px 14px;
    font-size: 12px;
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    border: 1px solid transparent;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    transition: background 0.1s;
  }

  .apv__sheet-tab:hover { background: var(--bg-hover); }

  .apv__sheet-tab--active {
    background: var(--bg-panel);
    border-color: var(--border);
    border-bottom-color: var(--bg-panel);
    color: var(--text-primary);
    font-weight: 500;
  }

  .apv__xlsx-body {
    flex: 1;
    overflow: auto;
    padding: 12px;
    background: #fff;
  }

  .apv__xlsx-body table {
    border-collapse: collapse;
    font-size: 13px;
    color: #1a1a1a;
  }

  .apv__xlsx-body td, .apv__xlsx-body th {
    border: 1px solid #d0d0d0;
    padding: 5px 10px;
    white-space: nowrap;
    font-family: monospace;
  }

  .apv__xlsx-body tr:nth-child(even) td { background: #f9f9f9; }
  .apv__xlsx-body tr:first-child td, .apv__xlsx-body th { background: #f0f0f0; font-weight: 600; }

  /* Text */
  .apv__text-container {
    height: 100%;
    overflow: auto;
    padding: 20px 24px;
  }

  .apv__text-body {
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
    font-size: 13px;
    line-height: 1.6;
    color: var(--text-primary);
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
  }

  /* Loading / error / unsupported */
  .apv__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 200px;
  }

  .apv__spinner {
    width: 28px;
    height: 28px;
    border: 3px solid var(--border);
    border-top-color: var(--brand);
    border-radius: 50%;
    animation: apv-spin 0.7s linear infinite;
  }

  @keyframes apv-spin {
    to { transform: rotate(360deg); }
  }

  .apv__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    height: 100%;
    min-height: 200px;
    color: var(--text-tertiary);
    padding: 32px;
    text-align: center;
  }

  .apv__error p {
    font-size: 13px;
    max-width: 360px;
  }

  .apv__unsupported {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 100%;
    min-height: 200px;
    padding: 40px;
    color: var(--text-tertiary);
  }

  .apv__unsupported-name {
    font-size: 16px;
    font-weight: 500;
    color: var(--text-primary);
    margin-top: 8px;
  }

  .apv__unsupported-msg {
    font-size: 13px;
  }

  .apv__unsupported-size {
    font-size: 12px;
  }

  /* Navigation arrows */
  .apv__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-secondary);
    cursor: pointer;
    transition: background 0.1s, opacity 0.1s;
    z-index: 10;
  }

  .apv__nav:hover { background: var(--bg-hover); }
  .apv__nav:disabled { opacity: 0.25; cursor: not-allowed; pointer-events: none; }

  .apv__nav--prev { left: 12px; }
  .apv__nav--next { right: 12px; }
`

// Inject styles once
let stylesInjected = false
function injectStyles() {
  if (stylesInjected) return
  stylesInjected = true
  const el = document.createElement('style')
  el.textContent = css
  document.head.appendChild(el)
}
injectStyles()
